import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { createSecureClonedName, sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import {
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    getComposerLinkOptionIDs,
    joinComposerLinkOptionIDs
} from './composerAttributesModels';

// LE OPZIONI CAMBIANO ATTRIBUTO TRASCINANDOLE (utente, 2026-10-15). Prima un'opzione si riordinava solo
// dentro il suo attributo, e per portarla altrove c'era la copia più la rimozione a mano. Ora una riga —
// o la selezione, anche di attributi diversi — cade davanti, dopo o dentro un altro attributo a opzioni.
//
// CHI ARRIVA DA UN ALTRO ATTRIBUTO È UN'OPZIONE NUOVA, come per «Copy to» e «Merge»: id nuovi, azioni e
// prezzi con lei, il codice reso unico. Spostare la stessa riga sotto un altro attributo lascerebbe al
// salvataggio un'opzione già salvata che cambia padre, e quel passaggio il salvataggio non lo conosce.
// I collegamenti che la nominavano la seguono: puntano al nuovo attributo e al nuovo id.

export const isComposerOptionAttribute = (attribute: ComposerAttributeNode) =>
    attribute.type === ComposerAttributeType.Attribute && attribute.attributeType !== MPlaza.AttributeType.CustomizableArea;

/** L'attributo a opzioni che contiene l'opzione. */
export const composerOptionOwner = (optionID: string) =>
    useComposerStore
        .getState()
        .attributes.find((attribute) => isComposerOptionAttribute(attribute) && attribute.options.some((option) => option.id === optionID));

const followMovedOptions = (links: ComposerAttributeNodeLink[], moved: Map<string, string>, targetID: string) =>
    links.map((link) => {
        const ids = getComposerLinkOptionIDs(link);
        if (!ids.some((id) => moved.has(id))) return link;
        // Tutte partite: la condizione segue le opzioni nel nuovo attributo.
        if (ids.every((id) => moved.has(id))) return { ...link, attributeID: targetID, optionID: joinComposerLinkOptionIDs(ids.map((id) => moved.get(id)!)) };
        // Solo alcune: nel vecchio attributo non si possono più scegliere, e la condizione le perde.
        return { ...link, optionID: joinComposerLinkOptionIDs(ids.filter((id) => !moved.has(id))) };
    });

/**
 * Porta le opzioni nell'attributo `targetID`, davanti o dopo `anchor` — senza, in coda —, nell'ordine dato.
 * Quelle che sono già lì si riordinano e basta. Restituisce le opzioni come stanno adesso.
 */
export const moveComposerOptions = (optionIDs: string[], targetID: string, anchor: { id: string; position: 'before' | 'after' } | null) => {
    const state = useComposerStore.getState();
    const target = state.attributes.find((attribute) => attribute.id === targetID);
    if (!target || !isComposerOptionAttribute(target)) return [];

    const all = state.attributes.filter(isComposerOptionAttribute).flatMap((attribute) => attribute.options);
    const carried = optionIDs.map((id) => all.find((option) => option.id === id)).filter((option): option is ComposerAttributeOptionNode => !!option);
    if (!carried.length) return [];

    // Le opzioni in arrivo: nuove, col codice libero nell'attributo che le accoglie.
    const usedCodes = target.options.map((option) => option.code);
    const newIds = new Map<string, string>();
    const placed = carried.map((option) => {
        if (option.attributeID === target.id) return option;
        const optionID = MPlaza.generateUUID();
        newIds.set(option.id, optionID);
        const code = createSecureClonedName(option.code, usedCodes);
        usedCodes.push(code);
        return {
            ...option,
            id: optionID,
            idNumeric: -1,
            code,
            attributeID: target.id,
            isDefault: false,
            isNew: true,
            actions: option.actions.map((action) => ({ ...action, id: MPlaza.generateUUID(), attributeID: target.id, optionID, isNew: true }))
        };
    });

    const rest = [...target.options].sort((left, right) => sortByDisplay(target.options, left, right)).filter((option) => !optionIDs.includes(option.id));
    const found = anchor ? rest.findIndex((option) => option.id === anchor.id) : -1;
    const at = found < 0 ? rest.length : anchor?.position === 'after' ? found + 1 : found;
    const ordered = [...rest.slice(0, at), ...placed, ...rest.slice(at)];
    // La predefinita resta quella che c'era; un attributo vuoto prende la prima arrivata.
    const hasDefault = ordered.some((option) => option.isDefault);
    const targetOptions = ordered.map((option, index) => ({ ...option, displayOrder: index, isDefault: hasDefault ? option.isDefault : index === 0 }));

    const movedPricings = state.attributes.flatMap((attribute) =>
        attribute.id === target.id
            ? []
            : attribute.pricings
                  .filter((pricing) => newIds.has(pricing.selectedOptionGuid))
                  .map((pricing) => ({ ...pricing, attributePricingID: 0, selectedOptionID: -1, selectedOptionGuid: newIds.get(pricing.selectedOptionGuid)! }))
    );

    const mentionsMoved = (attribute: ComposerAttributeNode) =>
        [attribute.links, ...attribute.options.map((option) => option.links)].some((links) =>
            links.some((link) => getComposerLinkOptionIDs(link).some((id) => newIds.has(id)))
        );
    const attributes = state.attributes.map((attribute) => {
        let next = attribute;
        if (attribute.id === target.id) next = { ...attribute, options: targetOptions, pricings: [...attribute.pricings, ...movedPricings] };
        else if (attribute.options.some((option) => newIds.has(option.id))) {
            const remaining = attribute.options.filter((option) => !newIds.has(option.id));
            const lostDefault = attribute.options.some((option) => option.isDefault && newIds.has(option.id));
            next = {
                ...attribute,
                options: lostDefault ? remaining.map((option, index) => ({ ...option, isDefault: index === 0 })) : remaining,
                pricings: attribute.pricings.filter((pricing) => !newIds.has(pricing.selectedOptionGuid))
            };
        }
        if (!newIds.size || !mentionsMoved(next)) return next;
        return {
            ...next,
            links: followMovedOptions(next.links, newIds, target.id),
            options: next.options.map((option) => ({ ...option, links: followMovedOptions(option.links, newIds, target.id) }))
        };
    });

    // Attributo per attributo, dalla porta di sempre: lo store li rivalida e tiene allineata la selezione.
    attributes.forEach((attribute, index) => attribute !== state.attributes[index] && state.updateAttribute(attribute));
    const updated = useComposerStore.getState().attributes.find((attribute) => attribute.id === target.id);
    return placed.map((option) => updated?.options.find((candidate) => candidate.id === option.id) ?? option);
};
